import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpParams,
  HttpRequest
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import * as Sentry from '@sentry/angular';
import { Observable, retry, tap, throwError, timer } from 'rxjs';

import { STORAGE_KEYS } from '../constants/storage-keys.const';
import { PostMessageTypesEnum } from '../enums/post-message-type.enum';
import { AuthService } from '../services/auth.service';
import { ErrorToastService } from '../services/error-toast.service';
import { CrdStateService } from '../services/state-service/crd-state.service';
import { EventStateService } from '../services/state-service/event-state.service';
import { UtilityService } from '../services/utility.service';
import {
  getHttpErrorMessage,
  isReadRequest
} from '../utils/error-message.util';
import {
  getRetryAfterDelay,
  isExpectedHttpError,
  LOCALLY_HANDLED_HTTP_ERROR,
  QUEUED_PLANTATION_DELETE
} from '../utils/http-error.util';

@Injectable()
export class CrdInterceptor implements HttpInterceptor {
  constructor(
    private readonly router: Router,
    private readonly authService: AuthService,
    private readonly crdStateService: CrdStateService,
    private readonly eventStateService: EventStateService,
    private readonly errorToastService: ErrorToastService,
    private readonly utilityService: UtilityService
  ) {}

  intercept(
    request: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    this.eventStateService.hasError = false;

    const authToken = `Token ${this.authService.userInfoToken}`;

    return this.modifiedHttpHandler(request, next, authToken);
  }

  private modifiedHttpHandler(
    request: HttpRequest<unknown>,
    next: HttpHandler,
    authToken: string
  ) {
    // Determine if we need to remove headers and params
    const modifiedRequest = this.createModifiedRequest(request, authToken);

    return next.handle(modifiedRequest).pipe(
      retry({
        delay: (error: unknown) => {
          const delay =
            request.method === 'DELETE' &&
            request.url.split('?')[0]?.endsWith('/v3/plantations/') &&
            !request.context.get(QUEUED_PLANTATION_DELETE)
              ? getRetryAfterDelay(error)
              : undefined;
          return delay === undefined ? throwError(() => error) : timer(delay);
        }
      }),
      tap({
        error: (error: unknown) => {
          if (error instanceof HttpErrorResponse) {
            this.handleHttpError(request, error, authToken);
          }
        }
      })
    );
  }

  private createModifiedRequest(
    request: HttpRequest<unknown>,
    authToken: string
  ): HttpRequest<unknown> {
    const setHeaders = {
      Authorization: authToken
    };

    if (request.headers.has('no-default-interceptor')) {
      return request.clone({
        headers: request.headers.delete('no-default-interceptor'),
        // params: new HttpParams(),  // Removes all query parameters
        setHeaders
      });
    }

    const params = this.getModifiedParams(request.params);

    return request.clone({
      setHeaders,
      params: this.shouldClearParams(request) ? new HttpParams() : params
    });
  }

  private getModifiedParams(originalParams: HttpParams): HttpParams {
    return originalParams
      .set('crd', this.crdStateService.crd)
      .set('plantation_table', this.crdStateService.plantationTable);
  }

  // TODO: refactor - remove
  private shouldClearParams(request: HttpRequest<unknown>): boolean {
    return [
      'v1/dataset',
      'plantation-data/validate',
      'plantations/?crd',
      'file-upload/columns',
      '/assets/',
      'assets.',
      'settings',
      request.url.includes('favorites') && request.method === 'POST'
    ].some((condition) => request.url.includes(String(condition)));
  }

  private handleHttpError(
    request: HttpRequest<unknown>,
    error: HttpErrorResponse,
    authToken: string
  ): void {
    if (
      request.context.get(QUEUED_PLANTATION_DELETE) &&
      getRetryAfterDelay(error) !== undefined
    )
      return;
    // Ignore an old session's 401 before touching the replacement session's UI.
    if (
      error.status === 401 &&
      authToken !== `Token ${this.authService.userInfoToken}`
    ) {
      return;
    }

    this.captureToSentry(request, error);

    // Features can own their error UI without bypassing session invalidation
    // or unexpected-error reporting. They still receive the original failure.
    const skipErrorUi = request.headers.has('skip-error-interceptor');
    if (!skipErrorUi || error.status === 401) {
      this.eventStateService.hasError = true;
      this.eventStateService.resetAllLoadingStates();
    }

    if (error.status === 401) {
      this.endSession();
    } else if (
      !skipErrorUi &&
      !request.context.get(LOCALLY_HANDLED_HTTP_ERROR) &&
      (error.status === 403 || !request.url.includes('v3/plantations'))
    ) {
      this.errorToastService.show(
        getHttpErrorMessage(error, isReadRequest(request.method, request.url))
      );
    }
  }

  // HCP sends the user to sign in; on its own, the app shows a quiet notice.
  private endSession(): void {
    this.utilityService.emitPropToParent(
      'type',
      PostMessageTypesEnum.SESSION_EXPIRED
    );
    this.router
      .navigate(['error'], { queryParams: { status: 401 } })
      .catch((error: unknown) => {
        console.error('Navigation failed.', error);
      });
    this.authService.userInfoToken = null;
    sessionStorage.removeItem(STORAGE_KEYS.FIREBASE_TOKEN);
    sessionStorage.removeItem(STORAGE_KEYS.FIREBASE_AUTH_TOKEN_EXPIRY);
  }

  private captureToSentry(
    request: HttpRequest<unknown>,
    error: HttpErrorResponse
  ): void {
    if (isExpectedHttpError(error)) return;
    if (error.status === 404 && error.message === 'Not found') return;

    const endpoint = request.url.replace(/\?.*$/s, '');

    const sentryError = new Error(
      `HTTP ${error.status} ${request.method} ${endpoint}`
    );
    sentryError.name = 'HttpError';

    Sentry.withScope((scope) => {
      scope.setTags({
        crd: this.crdStateService.crd || 'unknown',
        plantation_table: this.crdStateService.plantationTable || 'unknown'
      });
      scope.setContext('response', {
        status_code: error.status,
        detail: error.error?.detail ?? error.error,
        message: error.message
      });
      scope.setFingerprint([
        'http-error',
        request.method,
        endpoint,
        String(error.status)
      ]);
      Sentry.captureException(sentryError);
    });
  }
}
